import { useEffect, useState, useRef } from "react";
import { getPromotions } from "../api";
import { normalizePromotions } from "../pages/performance/utils";

export const usePromotionsData = (selectedProperty) => {
  const [promotionsData, setPromotionsData] = useState([]);
  const [promotionsLoading, setPromotionsLoading] = useState(false);
  const fetchingKeyRef = useRef(null);

  useEffect(() => {
    const propertyId = selectedProperty?.propertyId;

    if (!propertyId) {
      setPromotionsData([]);
      setPromotionsLoading(false);
      fetchingKeyRef.current = null;
      return;
    }

    const currency =
      selectedProperty?.acceptedCurrencies?.[0] ||
      selectedProperty?.currencyCode ||
      "USD";

    // Create a unique key for this fetch request
    const fetchKey = `${propertyId}-${currency}`;

    // Skip if already fetching the same data
    if (fetchingKeyRef.current === fetchKey) {
      // Already fetching, don't start another request
      // Loading state should already be true from the first call
      return;
    }

    // Set fetching key and loading state before making the API call
    fetchingKeyRef.current = fetchKey;
    setPromotionsLoading(true);
    const query = `?propertyId=${propertyId}&page=0&size=10&summary=true&limit=5`;
    getPromotions(query)
      .then((res) => {
        // Only check fetchKey to ensure this is still the current request
        // Don't check isMounted as it might be false from StrictMode's second effect cleanup
        if (fetchingKeyRef.current !== fetchKey) {
          return;
        }
        const apiData =
          res?.data?.data?.content ?? res?.data?.data ?? res?.data ?? [];
        setPromotionsData(normalizePromotions(apiData, currency));
      })
      .catch(() => {
        // Only check fetchKey to ensure this is still the current request
        if (fetchingKeyRef.current !== fetchKey) {
          return;
        }
        setPromotionsData([]);
      })
      .finally(() => {
        // Always update loading state if this is still the current fetch
        // Don't check isMounted here to ensure loading state is always cleared
        if (fetchingKeyRef.current === fetchKey) {
          setPromotionsLoading(false);
          fetchingKeyRef.current = null;
        }
      });

    return () => {
      // Don't reset fetchingKeyRef in cleanup to prevent race conditions with StrictMode
    };
  }, [
    selectedProperty?.propertyId,
    selectedProperty?.acceptedCurrencies,
    selectedProperty?.currencyCode,
  ]);

  return { promotionsData, promotionsLoading };
};
